import { useMemo } from 'react'
import type { PracticeAgentReportData } from '../../types'
import { classifyColumns, formatFieldReferencesInText, formatMetricValue, pickLabelColumn } from '../../utils'
import { BarViz } from './BarViz'
import { PaginationBar } from './PaginationBar'

export function KpiViz({
  data,
  onSelect,
  onPageChange,
  isGroup,
  mode = 'workspace',
}: {
  data: PracticeAgentReportData
  onSelect?: (field: string, value: string) => void
  onPageChange?: (p: number) => void
  isGroup?: boolean
  mode?: 'workspace' | 'dashboard'
}) {
  const { dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const labelCol = useMemo(() => pickLabelColumn(dimensionCols, data.rows), [dimensionCols, data.rows])
  const isDashboardMode = mode === 'dashboard'
  if (data.kpiCards.length === 0) return <BarViz data={data} onSelect={onSelect} onPageChange={onPageChange} />
  return (
    <div>
      <div className={`grid gap-4 py-2 ${isGroup ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {data.kpiCards.map((kpi, i) => (
          <div
            key={i}
            className={`rounded-2xl border p-5 transition-colors ${
              isDashboardMode
                ? 'border-brand-100 bg-gradient-to-br from-white via-white to-teal-50/40 shadow-card'
                : 'border-gray-100 bg-gray-50'
            } ${onSelect ? 'feature-hover-surface feature-hover-border cursor-pointer' : ''}`}
            onClick={() => onSelect?.(labelCol ?? '', kpi.label)}
          >
            <p className={`mb-2 text-xs font-medium uppercase tracking-[0.2em] ${isDashboardMode ? 'text-teal-700' : 'text-gray-500'}`}>{kpi.label}</p>
            <p className={`font-bold ${isDashboardMode ? 'text-3xl text-slate-950' : 'text-2xl text-gray-900'}`}>
              {formatMetricValue(kpi.value, kpi.measureLabel)}
              {kpi.unit && <span className="text-sm font-normal text-gray-400 ml-1">{kpi.unit}</span>}
            </p>
            {kpi.measureLabel && <p className={`mt-2 text-xs ${isDashboardMode ? 'text-slate-500' : 'text-gray-400'}`}>{formatFieldReferencesInText(kpi.measureLabel)}</p>}
          </div>
        ))}
      </div>
      {onPageChange && data.totalRows != null && data.page != null && data.pageSize != null && (
        <PaginationBar
          page={data.page}
          totalRows={data.totalRows}
          pageSize={data.pageSize}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
